import React, { ChangeEvent, FC } from 'react';
import {
	Grid,
	Card,
	CardHeader,
	CardContent,
	FormControl,
	FormGroup,
	FormHelperText,
	InputLabel,
	makeStyles,
	MenuItem,
	Select,
	TextField
} from '@material-ui/core';
import { AiringClientExclusions, Clients, Status } from '../../utils/types';
import { fetchFriendlyStatus } from '../../utils/typeTranslate';

const useStyles = makeStyles((theme) => ({
	inputField: {
		paddingBottom: theme.spacing(2)
	}
}));

export const AiringDetailsEditor: FC<AiringDetailsEditorProps> = (props) => {
	const { id, channelId, status, offset, clientExclusions, programSynopsis, programStartTime, programEndTime, onChange, onChangeMulti } = props;
	const classes = useStyles();

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} lg={6}>
				<Card>
					<CardHeader title='Airing details' />
					<CardContent>
						<FormGroup>

							<TextField id='id' label='ID' name='id' className={classes.inputField} value={id || ''} disabled fullWidth />

							<TextField id='channelId' label='Channel' name='channelId' className={classes.inputField} value={channelId || ''} disabled fullWidth />

							<FormControl style={{ marginBottom: '1.5rem' }}>
								<InputLabel shrink id='statusLabel'>
									Status
								</InputLabel>
								<Select
									id='status'
									labelId='statusLabel'
									name='status'
									defaultValue={status}
									onChange={onChangeMulti}
									fullWidth
									MenuProps={{
										getContentAnchorEl: null,
										PaperProps: {
											style: {
												maxHeight: 250
											}
										}
									}}>
									{Object.values(Status).map((key, idx) => (
										<MenuItem value={Status[key]} key={`Client${idx}`}>
											{fetchFriendlyStatus(Status[key])}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									Changing the status of an Airing <strong>may not</strong> have the effect you expect. The background system may yet update this
									status inspite of your change.
								</FormHelperText>
							</FormControl>

							<TextField
								id='offset'
								label='Offset (Seconds)'
								name='offset'
								type='number'
								className={classes.inputField}
								value={offset || ''}
								onChange={onChange}
								fullWidth
								focused
							/>

							<FormControl className={classes.inputField}>
								<InputLabel shrink id='clientExclusionsLabel'>
									Client Exclusions
								</InputLabel>
								<Select
									id='clientExclusions'
									labelId='clientExclusionsLabel'
									name='clientExclusions'
									value={clientExclusions}
									onChange={onChangeMulti}
									fullWidth
									multiline
									multiple
									MenuProps={{
										getContentAnchorEl: null,
										PaperProps: {
											style: {
												maxHeight: 250
											}
										}
									}}>
									{Object.values(Clients).map((key, idx) => (
										<MenuItem value={Clients[key]} key={`Client${idx}`}>
											{Clients[key]}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>Select clients for which this airing will be excluded in API requests</FormHelperText>
							</FormControl>

							<TextField
								id='programSynopsis'
								label='Program Synopsis'
								name='programSynopsis'
								className={classes.inputField}
								value={programSynopsis}
								fullWidth
								multiline
								maxRows={2}
								onChange={onChange}
							/>

							<TextField
								id='programStartTime'
								label='Program Start Time'
								name='programStartTime'
								className={classes.inputField}
								value={programStartTime}
								onChange={onChange}
							/>

							<TextField
								id='programEndTime'
								label='Program End Time'
								name='programEndTime'
								className={classes.inputField}
								value={programEndTime}
								onChange={onChange}
								helperText={<>Changing the program synopsis, start time or end time <strong>may not</strong> have the effect you expect. The background system may yet update these values inspite of your change.</>}
							/>

						</FormGroup>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};

type AiringDetailsEditorProps = {
	id: string;
	channelId: string | null;
	status: Status;
	offset: string | null;
	clientExclusions?: AiringClientExclusions | null;
	programSynopsis?: string;
	programStartTime?: string;
	programEndTime?: string;
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
	onChangeMulti?: (event: ChangeEvent<{ value: unknown }>) => void;
};

export default AiringDetailsEditor;
